import { useCallback, useEffect, useRef, useState } from "react";
import posthog from "posthog-js";
import { UseFormReturn } from "react-hook-form";
import { toast } from "sonner";

import { JobFormData } from "@/lib/types";

export type JobStep =
  | "basic"
  | "skills"
  | "quiz"
  | "customize"
  | "share"
  | "insights";

interface JobStateMachineConfig {
  initialStep: JobStep;
  savedJobId?: string;
  workspaceId?: string;
  saveJobData: (data: any) => Promise<any>;
  onSaveSuccess?: (step: JobStep, data: any) => void;
  onSaveError?: (step: JobStep, error: any) => void;
  formMethods: UseFormReturn<JobFormData>; // Add form methods as a required parameter
}

export function useJobStateMachine({
  initialStep,
  savedJobId,
  workspaceId,
  saveJobData,
  onSaveSuccess,
  onSaveError,
  formMethods, // Get form methods from parameter
}: JobStateMachineConfig) {
  const [loadedSteps, setLoadedSteps] = useState<Set<JobStep>>(new Set());
  const [currentStep, setCurrentStep] = useState<JobStep>(initialStep);
  const [previousStep, setPreviousStep] = useState<JobStep | null>(null);
  const [isSaving, setIsSaving] = useState(false);
  const [isValidating, setIsValidating] = useState(false);
  const [hasJobId, setHasJobId] = useState(Boolean(savedJobId));
  const [hasSkills, setHasSkills] = useState(false);
  const [hasQuiz, setHasQuiz] = useState(false);
  const [lastSaved, setLastSaved] = useState<Record<JobStep, Date | null>>({
    basic: null,
    skills: null,
    quiz: null,
    customize: null,
    share: null,
    insights: null,
  });

  const [dirtySections, setDirtySections] = useState<Record<string, boolean>>(
    {}
  );
  const markSectionDirty = useCallback((section: string, isDirty: boolean) => {
    setDirtySections((prev) => ({
      ...prev,
      [section]: isDirty,
    }));
  }, []);

  // Extract methods from formMethods
  const {
    formState: { isDirty, dirtyFields, errors },
    getValues,
    setValue,
    trigger,
    reset,
    watch,
  } = formMethods;

  // Map steps to their validation fields
  const stepValidationFields = useRef({
    basic: [
      "basic.title",
      "basic.departmentId",
      "basic.experienceStart",
      "basic.experienceEnd",
    ],
    skills: [], // No mandatory validation for skills, can be empty
    quiz: [], // No mandatory validation for quiz generation
    customize: [], // Customization has subforms with their own validation
    share: [], // No required fields in sharing
    insights: [], // No validation needed for insights
  });

  // Map to track which steps are allowed to navigate to
  const canNavigateToStep = useCallback(
    (step: JobStep): boolean => {
      switch (step) {
        case "basic":
          return true; // Can always navigate to first step
        case "skills":
          return hasJobId; // Need job ID to save skills
        case "quiz":
          return hasJobId; // Need skills to generate quiz
        case "customize":
          return hasJobId; // Need job ID
        case "share":
          return hasJobId; // Need job ID to share
        case "insights":
          return hasJobId; // Need job ID to show insights
        default:
          return false;
      }
    },
    [hasJobId, hasSkills]
  );

  // Update job ID state when savedJobId changes
  useEffect(() => {
    if (savedJobId) {
      setHasJobId(true);
    }
  }, [savedJobId]);

  // Add a tracking function
  const trackEvent = useCallback(
    (eventName: string, properties: Record<string, any> = {}) => {
      posthog.capture(eventName, {
        step: currentStep,
        job_id: savedJobId,
        ...properties,
      });
    },
    [currentStep, savedJobId]
  );

  // Validate fields for the current step
  const validateStep = useCallback(
    async (step: JobStep): Promise<boolean> => {
      const fieldsToValidate = stepValidationFields.current[step];
      if (fieldsToValidate.length === 0) return true;

      setIsValidating(true);
      try {
        const isValid = await trigger(fieldsToValidate as any);
        return isValid;
      } catch (error) {
        console.error(`Validation error in ${step}:`, error);
        return false;
      } finally {
        setIsValidating(false);
      }
    },
    [trigger]
  );

  const resetStep = useCallback(
    (step: JobStep): void => {
      // This resets a whole step's form state to the last saved values
      // We'll use formMethods.getValues() to get current server-side values
      const currentValues = formMethods.getValues();

      switch (step) {
        case "basic":
          formMethods.setValue("basic", currentValues.basic || {}, {
            shouldDirty: false,
          });
          break;

        case "skills":
          formMethods.setValue(
            "skills",
            currentValues.skills || {
              technicalSkills: [],
              cognitiveSkills: [],
            },
            { shouldDirty: false }
          );
          break;

        case "quiz":
          formMethods.setValue(
            "pandaQuiz",
            currentValues.pandaQuiz || { questions: [] },
            { shouldDirty: false }
          );
          break;

        case "customize":
          // Reset all customization fields
          formMethods.setValue(
            "screeningQuestions",
            currentValues.screeningQuestions || [],
            { shouldDirty: false }
          );
          formMethods.setValue(
            "proctoringSettings",
            currentValues.proctoringSettings || {
              PROCTORING_ENABLED: false,
              PROCTORING_MULTIFACE_DETECTION: true,
              PROCTORING_VOICE_MONITORING: true,
              PROCTORING_EYE_TRACKING: true,
              PROCTORING_LIVENESS_ENABLED: true,
              PROCTORING_PERIODIC_SNAPSHOTS: true,
              PROCTORING_RED_FLAG_SNAPSHOTS: true,
              PROCTORING_BLOCK_SCREEN_SHARING: true,
              PROCTORING_OBJECT_DETECTION: true,
              PROCTORING_BROWSER_FOCUS: true,
            },
            { shouldDirty: false }
          );
          formMethods.setValue(
            "submissionSettings",
            currentValues.submissionSettings || {
              QUIZSUBMISSION_RESUME_UPLOAD: false,
              QUIZSUBMISSION_SHARE_RESULTS: "NEVER",
              QUIZSUBMISSION_EMAIL_CONFIRMATION: false,
              QUIZSUBMISSION_NOTIFY_HIRING_TEAM: false,
            },
            { shouldDirty: false }
          );
          formMethods.setValue(
            "videoResume",
            currentValues.videoResume || {
              QUIZSUBMISSION_VIDEO_RESUME: false,
              QUIZSUBMISSION_VIDEO_RESUME_INSTRUCTIONS: "",
              QUIZSUBMISSION_VIDEO_RESUME_MAXDURATION: "",
            },
            { shouldDirty: false }
          );
          formMethods.setValue(
            "jobDetails",
            currentValues.jobDetails || {
              JOBINFORMATION_SALARYCURRENCY: "INR",
              JOBINFORMATION_SALARYMIN: "",
              JOBINFORMATION_SALARYMAX: "",
              JOBINFORMATION_WORKTYPE: "",
              JOBINFORMATION_WORKMODE: "",
              JOBINFORMATION_WORKLOCATION: "",
              JOBINFORMATION_SHORTDESCRIPTION: "",
              JOBINFORMATION_PERKSBENEFITS: "",
              JOBINFORMATION_OGDESCRIPTION: "",
            },
            { shouldDirty: false }
          );
          formMethods.setValue(
            "formDetails",
            currentValues.formDetails || {
              FORMSETTINGS_RESUMEREQUIRED: false,
              FORMSETTINGS_CONTACTNUMBEREQUIRED: false,
              FORMSETTINGS_LOCATIONREQUIRED: false,
              FORMSETTINGS_LINKEDINREQUIRED: false,
              FORMSETTINGS_GITHUBREQUIRED: false,
              FORMSETTINGS_EDUCATIONREQUIRED: false,
              FORMSETTINGS_CTCREQUIRED: false,
              FORMSETTINGS_CANDIDATECONSENTREQUIRED: false,
              FORMSETTINGS_CONSENTTEXT: "",
              ISREQUIRED_FORMSETTINGS_RESUMEREQUIRED: false,
              ISREQUIRED_FORMSETTINGS_CONTACTNUMBEREQUIRED: false,
              ISREQUIRED_FORMSETTINGS_LOCATIONREQUIRED: false,
              ISREQUIRED_FORMSETTINGS_LINKEDINREQUIRED: false,
              ISREQUIRED_FORMSETTINGS_GITHUBREQUIRED: false,
              ISREQUIRED_FORMSETTINGS_EDUCATIONREQUIRED: false,
              ISREQUIRED_FORMSETTINGS_CTCREQUIRED: false,
              ISREQUIRED_FORMSETTINGS_CANDIDATECONSENTREQUIRED: false,
              ISREQUIRED_FORMSETTINGS_CONSENTTEXT: false,
            },
            { shouldDirty: false }
          );
          formMethods.setValue(
            "referralRewards",
            currentValues.referralRewards || {
              REFERRAL_REWARDTYPE: "CASH",
              REFERRAL_DETAILS: "",
              REFERRAL_CURRENCY: "USD",
              REFERRAL_AMOUNT: "",
              REFERRAL_MUSTBEHIRED: false,
              REFERRAL_MUSTCOMPLETEPROBATION: false,
            },
            { shouldDirty: false }
          );
          formMethods.setValue(
            "quizSettings",
            currentValues.quizSettings || {
              QUIZ_SHARERESULTS: "NEVER",
              QUIZ_TIMELIMIT: "30",
              QUIZ_PASSINGSCORE: "70",
              QUIZ_RANDOMIZEQUESTIONS: false,
              QUIZ_ALLOWMULTIPLEATTEMPTS: false,
              QUIZ_SHOWCORRECTANSWERS: false,
            },
            { shouldDirty: false }
          );
          break;

        case "share":
          formMethods.setValue(
            "pandaQuizShare",
            currentValues.pandaQuizShare || {},
            { shouldDirty: false }
          );
          break;
      }

      // Track the reset event
      trackEvent("job_builder_reset_step", {
        step_name: step,
        reset_reason: "user_discard_changes",
      });
    },
    [formMethods, trackEvent]
  );

  // Save data for the current step
  const saveStep = useCallback(
    async (step: JobStep, force = false): Promise<boolean> => {
      // If the form is not dirty and we're not forcing a save, skip
      if (!isDirty && !force) return true;

      // Track the validation attempt
      trackEvent("job_builder_validate_step", {
        step_name: step,
        has_job_id: Boolean(savedJobId),
      });

      // Validate before saving
      const isValid = await validateStep(step);
      if (!isValid) {
        trackEvent("job_builder_validation_failed", {
          step_name: step,
          errors: Object.keys(errors),
        });

        toast.error(`Please fix the errors before proceeding.`);
        return false;
      }

      // Track the save attempt
      trackEvent("job_builder_save_step", {
        step_name: step,
        is_new: !savedJobId,
      });

      setIsSaving(true);
      try {
        const formData = getValues();
        let dataToSave;

        // Only send the relevant portion of the form data based on the step
        switch (step) {
          case "basic":
            dataToSave = { basic: formData.basic };
            break;
          case "skills":
            dataToSave = {
              skills: formData.skills,
            };
            break;
          case "quiz":
            dataToSave = { pandaQuiz: formData.pandaQuiz };
            break;
          case "customize":
            dataToSave = {
              proctoringSettings: formData.proctoringSettings,
              submissionSettings: formData.submissionSettings,
              formDetails: formData.formDetails,
              jobDetails: formData.jobDetails,
              screeningQuestions: formData.screeningQuestions,
              videoResume: formData.videoResume,
              referralRewards: formData.referralRewards,
              quizSettings: formData.quizSettings,
            };
            break;
          default:
            dataToSave = formData;
        }

        const result = await saveJobData(dataToSave);
        if (result) {
          // Ensure we merge the result back into the form without marking as dirty
          if (typeof result === "object") {
            // Merge the result back into the form state
            for (const key in result) {
              if (Object.prototype.hasOwnProperty.call(result, key)) {
                setValue(key as any, result[key], { shouldDirty: false });
              }
            }
          }
          // Reset dirty state for saved sections
          Object.keys(dataToSave).forEach((section) => {
            markSectionDirty(section, false);
          });
        }
        // Track successful save
        trackEvent("job_builder_save_success", {
          step_name: step,
          data_size: JSON.stringify(dataToSave).length,
        });

        // Update state based on successful save
        setLastSaved((prev) => ({ ...prev, [step]: new Date() }));

        if (step === "basic" && result?.id) {
          setHasJobId(true);
        } else if (step === "skills") {
          setHasSkills(Boolean(formData.skills?.technicalSkills?.length));
        } else if (step === "quiz") {
          setHasQuiz(Boolean(formData.pandaQuiz?.questions?.length));
        }

        onSaveSuccess?.(step, result);
        return true;
      } catch (error) {
        // Track save error
        trackEvent("job_builder_save_error", {
          step_name: step,
          error_message:
            error instanceof Error ? error.message : "Unknown error",
        });

        console.error(`Error saving ${step}:`, error);
        onSaveError?.(step, error);
        toast.error(
          `Failed to save ${step.charAt(0).toUpperCase() + step.slice(1)} data`
        );
        return false;
      } finally {
        setIsSaving(false);
      }
    },
    [
      isDirty,
      validateStep,
      getValues,
      saveJobData,
      onSaveSuccess,
      onSaveError,
      trackEvent,
      errors,
      savedJobId,
      dirtySections,
      setValue,
      markSectionDirty,
    ]
  );

  // Navigate to a step with validation and saving
  const navigateToStep = useCallback(
    async (
      stepOrDirection: JobStep | "next" | "prev",
      forceSave = false
    ): Promise<boolean> => {
      // Define the sequence of steps
      const steps: JobStep[] = [
        "basic",
        "skills",
        "quiz",
        "customize",
        "share",
        "insights",
      ];

      // Determine the target step based on input
      let targetStep: JobStep;
      if (stepOrDirection === "next") {
        const currentIndex = steps.indexOf(currentStep);
        if (currentIndex >= steps.length - 1) return false;
        targetStep = steps[currentIndex + 1];
      } else if (stepOrDirection === "prev") {
        const currentIndex = steps.indexOf(currentStep);
        if (currentIndex <= 0) return false;
        targetStep = steps[currentIndex - 1];
      } else {
        targetStep = stepOrDirection;
      }

      // Track step navigation attempt
      trackEvent("job_builder_navigate_attempt", {
        from_step: currentStep,
        to_step: targetStep,
        is_allowed: canNavigateToStep(targetStep),
      });

      if (!loadedSteps.has(targetStep)) {
        setLoadedSteps((prev) => new Set([...prev, targetStep]));
      }

      const isFirstSave =
        currentStep === "basic" &&
        targetStep === "skills" &&
        !hasJobId &&
        isDirty;

      if (isFirstSave) {
        trackEvent("job_builder_first_save", {
          form_data_size: JSON.stringify(getValues("basic")).length,
        });

        // Validate and save the basic step
        const isValid = await validateStep("basic");
        if (!isValid) {
          toast.error("Please fix the errors before continuing");
          return false;
        }

        if (!getValues("basic.key")) {
          const domain = process.env.NEXT_PUBLIC_APP_SHORT_DOMAIN!;
          const res = await fetch(
            `/api/v1/links/random?domain=${domain}&workspaceId=${workspaceId}`
          );
          if (res.ok) {
            const key = await res.json();
            setValue("basic.key", key, { shouldDirty: true });
            setValue("basic.url", `https://${domain}/quiz/${key}`, {
              shouldDirty: true,
            });
          }
        }

        setIsSaving(true);
        try {
          const basicData = { basic: getValues("basic") };
          const result = await saveJobData(basicData);

          if (result?.id) {
            // Update state with the new job ID
            setHasJobId(true);

            // Now we can move to the skills step
            setPreviousStep(currentStep);
            setCurrentStep(targetStep);

            toast.success("Job created successfully!");
            return true;
          } else {
            toast.error("Failed to create job");
            return false;
          }
        } catch (error) {
          console.error("Error creating job:", error);
          toast.error("Failed to create job");
          return false;
        } finally {
          setIsSaving(false);
        }
      }

      // Check if navigation is allowed
      if (!canNavigateToStep(targetStep)) {
        let reason = "";
        if (targetStep === "skills" && !hasJobId)
          reason = "You need to save the basic details first";
        if (targetStep === "quiz" && !hasSkills)
          reason = "You need to define skills first";

        trackEvent("job_builder_navigation_blocked", {
          from_step: currentStep,
          to_step: targetStep,
          reason,
        });

        toast.error(reason || `Cannot navigate to ${targetStep} at this time`);
        return false;
      }

      // If dirty, prompt to save or save automatically based on configuration
      if (isDirty || forceSave) {
        const shouldSave =
          forceSave || window.confirm("Save changes before continuing?");

        if (shouldSave) {
          trackEvent("job_builder_save_before_navigate", {
            from_step: currentStep,
            to_step: targetStep,
          });

          const saveResult = await saveStep(currentStep, true);
          if (!saveResult) return false;
        } else {
          resetStep(currentStep);
          trackEvent("job_builder_discard_changes", {
            from_step: currentStep,
            to_step: targetStep,
          });
        }
      }

      // Track successful navigation
      trackEvent("job_builder_navigate_success", {
        from_step: currentStep,
        to_step: targetStep,
      });

      // Update step state
      setPreviousStep(currentStep);
      setCurrentStep(targetStep);
      return true;
    },
    [
      canNavigateToStep,
      isDirty,
      saveStep,
      currentStep,
      trackEvent,
      hasJobId,
      hasSkills,
      validateStep,
      saveJobData,
      getValues,
      savedJobId,
      resetStep,
    ]
  );

  // Move to next step
  const nextStep = useCallback(
    async (): Promise<boolean> => navigateToStep("next", true),
    [navigateToStep]
  );

  const prevStep = useCallback(
    async (): Promise<boolean> => navigateToStep("prev"),
    [navigateToStep]
  );

  // Update skill state when form data changes
  useEffect(() => {
    const checkFormState = () => {
      const formData = getValues();
      const hasSkillsNow = Boolean(formData.skills?.technicalSkills?.length);
      const hasQuizNow = Boolean(formData.pandaQuiz?.questions?.length);

      if (hasSkillsNow !== hasSkills) {
        setHasSkills(hasSkillsNow);
      }

      if (hasQuizNow !== hasQuiz) {
        setHasQuiz(hasQuizNow);
      }
    };

    // Initial check
    checkFormState();

    // Set up a watch subscription to detect changes
    const subscription = watch(() => {
      checkFormState();
    });

    // Cleanup subscription
    return () => subscription.unsubscribe();
  }, [getValues, hasSkills, hasQuiz, watch]);

  return {
    currentStep,
    previousStep,
    isSaving,
    isValidating,
    hasJobId,
    hasSkills,
    hasQuiz,
    lastSaved,
    canNavigateToStep,
    validateStep,
    saveStep,
    navigateToStep,
    nextStep,
    prevStep,
    trackEvent,
    loadedSteps,
  };
}
